import React, { useState, useEffect } from 'react';
import { getUserFriendlyError } from '../utils/errorMessages';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, Sparkles, Zap, Loader2, ArrowLeft, Globe } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import EmbeddedCheckoutModal from '../components/EmbeddedCheckout';

const Pricing = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const { locale, pricing, formatPrice } = useLocale();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    loadPlans();
    loadUserSubscription();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/plans`, {
        credentials: 'include'
      });
      const data = await response.json();
      setPlans(data);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserSubscription = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/subscription`, {
        credentials: 'include'
      });
      const data = await response.json();
      setUserSubscription(data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const handleSubscribe = (planId) => {
    // Get plan name for display
    const planName = planId === 'pro_monthly' ? 'Pro Monthly' : 'Pro Annual';
    setSelectedPlan({ id: planId, name: planName });
    setCheckoutOpen(true);
  };

  const handleCheckoutSuccess = () => {
    toast({
      title: '🎉 Payment Successful!',
      description: 'Welcome to Pro! Your subscription is now active.'
    });
    // Refresh user data to get updated subscription
    if (refreshUser) refreshUser();
    loadUserSubscription();
    navigate('/payment-success');
  };

  const handleCheckoutClose = () => {
    setCheckoutOpen(false);
    setSelectedPlan(null);
    setProcessingPlan(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-white p-4">
      <div className="max-w-7xl mx-auto">
        <Button
          onClick={() => navigate('/home')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Plan</h1>
          <p className="text-lg text-gray-600">Unlock the full power of AI with Allora</p>
          
          {/* Regional pricing indicator */}
          <div className="mt-3 inline-flex items-center gap-2 text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">
            <Globe className="h-4 w-4" />
            <span>Prices shown in {locale.currency} ({locale.country})</span>
          </div>
          
          {userSubscription && (
            <div className="mt-4 inline-flex items-center gap-2 bg-teal-100 text-teal-800 px-4 py-2 rounded-full">
              <Sparkles className="h-4 w-4" />
              <span className="font-medium">
                Current: {userSubscription.subscription?.plan_name || 'Free'} | Credits: {userSubscription.credits || 0}
              </span>
            </div>
          )}
        </div>

        {/* Subscription Plans */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-center mb-8">Subscription Plans</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Free Plan */}
            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-2xl">Free</CardTitle>
                <div className="text-3xl font-bold mt-2">{pricing?.symbol || '$'}0<span className="text-sm text-gray-500">/month</span></div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Basic task management</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Health & medicine reminders</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Bill & appointment tracking</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">10 free AI credits</span>
                  </li>
                </ul>
                <Button variant="outline" className="w-full" disabled>
                  Current Plan
                </Button>
              </CardContent>
            </Card>

            {/* Pro Monthly */}
            <Card className="border-2 border-teal-600 hover:shadow-xl transition-shadow relative">
              <Badge className="absolute top-4 right-4 bg-teal-600">Popular</Badge>
              <CardHeader>
                <CardTitle className="text-2xl">Pro Monthly</CardTitle>
                <div className="text-3xl font-bold mt-2">
                  {formatPrice ? formatPrice(pricing?.monthly || 9.99) : `${pricing?.symbol || '$'}${pricing?.monthly || 9.99}`}
                  <span className="text-sm text-gray-500">/month</span>
                </div>
                <p className="text-sm text-green-600 font-medium">7-day free trial</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">Everything in Free</span>
                  </li>
                  <li className="flex items-start">
                    <Sparkles className="h-5 w-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">Unlimited AI document analysis</span>
                  </li>
                  <li className="flex items-start">
                    <Sparkles className="h-5 w-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">Unlimited food scanning</span>
                  </li>
                  <li className="flex items-start">
                    <Sparkles className="h-5 w-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">Unlimited AI chat</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-teal-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Priority support</span>
                  </li>
                </ul>
                <Button
                  onClick={() => handleSubscribe('pro_monthly')}
                  disabled={processingPlan === 'pro_monthly'}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                >
                  {processingPlan === 'pro_monthly' ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                  ) : (
                    (userSubscription?.trial_used || userSubscription?.subscription?.status === 'trial') ? 'Subscribe Now' : 'Start Free Trial'
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Pro Annual */}
            <Card className="border-2 hover:shadow-lg transition-shadow relative">
              <Badge className="absolute top-4 right-4 bg-green-600">Best Value</Badge>
              <CardHeader>
                <CardTitle className="text-2xl">Pro Annual</CardTitle>
                <div className="text-3xl font-bold mt-2">
                  {formatPrice ? formatPrice(pricing?.yearly || 99.99) : `${pricing?.symbol || '$'}${pricing?.yearly || 99.99}`}
                  <span className="text-sm text-gray-500">/year</span>
                </div>
                <p className="text-sm text-green-600 font-medium">Save 2 months + 7-day trial</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">Everything in Pro Monthly</span>
                  </li>
                  <li className="flex items-start">
                    <Zap className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">2 months free ({formatPrice ? formatPrice(pricing?.monthly * 2 || 19.98) : `${pricing?.symbol || '$'}${(pricing?.monthly * 2) || 19.98}`} savings)</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Lock in current pricing</span>
                  </li>
                </ul>
                <Button
                  onClick={() => handleSubscribe('pro_annual')}
                  disabled={processingPlan === 'pro_annual'}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {processingPlan === 'pro_annual' ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                  ) : (
                    (userSubscription?.trial_used || userSubscription?.subscription?.status === 'trial') ? 'Subscribe Now' : 'Start Free Trial'
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Feature Costs */}
        <div className="mt-12 bg-white rounded-lg p-6 shadow-sm max-w-2xl mx-auto">
          <h3 className="text-lg font-semibold mb-4 text-center">Credit Costs</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-teal-600">{plans?.ai_costs?.document_analysis}</div>
              <div className="text-sm text-gray-600">Document Analysis</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">{plans?.ai_costs?.food_scan}</div>
              <div className="text-sm text-gray-600">Food Scan</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{plans?.ai_costs?.chat_message}</div>
              <div className="text-sm text-gray-600">Chat Message</div>
            </div>
          </div>
        </div>
      </div>

      {/* Embedded Checkout Modal */}
      {selectedPlan && (
        <EmbeddedCheckoutModal
          isOpen={checkoutOpen}
          onClose={handleCheckoutClose}
          planId={selectedPlan.id}
          planName={selectedPlan.name}
          onSuccess={handleCheckoutSuccess}
        />
      )}
    </div>
  );
};

export default Pricing;